import { formatAgeLabel, formatUkDateOfBirth } from '../family/dateOfBirth';
import { formatPetTypeLabel } from '../health/health-subjects';

export type SharedPetProfileDraft = {
  name: string;
  petType: string;
  breed: string;
  dateOfBirth: string;
  ageNotes: string;
  microchipNumber: string;
  adoptionDate: string;
  weightKg: string;
  healthConditionsSummary: string;
  insuranceSummary: string;
  vetPracticeSummary: string;
  notes: string;
};

export type SharedPetProfileSummaryInput = {
  petType?: string | null;
  breed?: string | null;
  dateOfBirth?: string | null;
  ageNotes?: string | null;
  microchipNumber?: string | null;
  adoptionDate?: string | null;
  weightKg?: number | null;
  healthConditionsSummary?: string | null;
  insuranceSummary?: string | null;
  vetPracticeSummary?: string | null;
};

export type SharedPetHubCountsInput = {
  reminderCount?: number | null;
  dueReminderCount?: number | null;
  medicationCount?: number | null;
};

export const EMPTY_SHARED_PET_PROFILE_DRAFT: SharedPetProfileDraft = {
  name: '',
  petType: 'dog',
  breed: '',
  dateOfBirth: '',
  ageNotes: '',
  microchipNumber: '',
  adoptionDate: '',
  weightKg: '',
  healthConditionsSummary: '',
  insuranceSummary: '',
  vetPracticeSummary: '',
  notes: '',
};

function trimOptionalString(value: string) {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

export function parsePetWeightKg(value: string) {
  const cleaned = value.replace(/,/g, '.').trim();
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Number(parsed.toFixed(2));
}

export function formatPetWeightKg(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) {
    return null;
  }

  return `${value.toFixed(2).replace(/\.?0+$/, '')}kg`;
}

export function buildPetProfilePayload(input: {
  userId: string;
  draft: SharedPetProfileDraft;
}) {
  return {
    user_id: input.userId,
    name: input.draft.name.trim(),
    pet_type: input.draft.petType.trim(),
    breed: trimOptionalString(input.draft.breed),
    dob: input.draft.dateOfBirth || null,
    age_notes: trimOptionalString(input.draft.ageNotes),
    microchip_number: trimOptionalString(input.draft.microchipNumber),
    adoption_date: input.draft.adoptionDate || null,
    weight_kg: parsePetWeightKg(input.draft.weightKg),
    health_conditions_summary: trimOptionalString(input.draft.healthConditionsSummary),
    insurance_summary: trimOptionalString(input.draft.insuranceSummary),
    vet_practice_summary: trimOptionalString(input.draft.vetPracticeSummary),
    notes: trimOptionalString(input.draft.notes),
  };
}

export function buildPetProfileSummary(pet: SharedPetProfileSummaryInput) {
  return [
    pet.petType ? formatPetTypeLabel(pet.petType) : null,
    formatAgeLabel(pet.dateOfBirth || null),
    pet.breed ? `Breed: ${pet.breed}` : null,
    pet.dateOfBirth ? `DOB ${formatUkDateOfBirth(pet.dateOfBirth)}` : null,
    pet.adoptionDate ? `Adopted ${formatUkDateOfBirth(pet.adoptionDate)}` : null,
    formatPetWeightKg(pet.weightKg),
    pet.microchipNumber ? 'Microchip saved' : null,
    pet.healthConditionsSummary ? 'Health notes saved' : null,
    pet.insuranceSummary ? 'Insurance saved' : null,
    pet.vetPracticeSummary ? 'Vet saved' : null,
    pet.ageNotes || null,
  ].filter(Boolean);
}

function formatCountLabel(value: number, singular: string, plural = `${singular}s`) {
  return `${value} ${value === 1 ? singular : plural}`;
}

export function buildPetHubSignals(input: SharedPetHubCountsInput) {
  const reminderCount = Math.max(0, Number(input.reminderCount || 0));
  const dueReminderCount = Math.max(0, Number(input.dueReminderCount || 0));
  const medicationCount = Math.max(0, Number(input.medicationCount || 0));
  const parts: string[] = [];

  if (dueReminderCount > 0) {
    parts.push(formatCountLabel(dueReminderCount, 'due reminder'));
  } else if (reminderCount > 0) {
    parts.push(formatCountLabel(reminderCount, 'reminder'));
  }

  if (medicationCount > 0) {
    parts.push(formatCountLabel(medicationCount, 'medication'));
  }

  return parts.length ? parts : ['Ready for care reminders'];
}

export function buildPetHubNextStep(
  input: SharedPetHubCountsInput & Pick<SharedPetProfileSummaryInput, 'healthConditionsSummary' | 'insuranceSummary' | 'vetPracticeSummary'>,
) {
  const reminderCount = Math.max(0, Number(input.reminderCount || 0));
  const dueReminderCount = Math.max(0, Number(input.dueReminderCount || 0));
  const medicationCount = Math.max(0, Number(input.medicationCount || 0));

  if (dueReminderCount > 0) {
    return 'My Health already has pet reminders waiting for you.';
  }

  if (reminderCount > 0 || medicationCount > 0) {
    return 'My Health is already holding the ongoing care details for this pet.';
  }

  if (input.healthConditionsSummary || input.insuranceSummary || input.vetPracticeSummary) {
    return 'The saved basics are already in place for later care reminders.';
  }

  return 'Add care reminders in My Health when you want Adulting to keep nudging you.';
}

export function hasTrackedPets<T extends { name?: string | null }>(pets?: readonly T[] | null) {
  if (!Array.isArray(pets) || pets.length === 0) {
    return false;
  }

  return pets.some((pet) => Boolean(pet.name?.trim()));
}
